import React, { useMemo } from 'react'
import { Box, Chip, IconButton, Tooltip, Typography, useTheme } from '@mui/material'
import { Timeline as TimelineIcon, Flag as FlagIcon, PhotoCamera as PhotoCameraIcon } from '@mui/icons-material'
import { OtwAgGridReact as AgGridReact } from '@/components/AgGrid/OtwAgGridReact'
import { ColDef, ICellRendererParams } from 'ag-grid-community'
import 'ag-grid-community/styles/ag-grid.css'
import 'ag-grid-community/styles/ag-theme-alpine.css'
import { MobileRecordAction, MobileRecordCard } from 'components/MobileRecordCard'
import { MobileRecordList } from 'components/MobileRecordList'
import { useIsMobile } from 'hooks/screen/useIsMobile'
import { legStatusTone } from '../../utils/mobileStatusTones'

export interface TransportersLegRow {
  id: string
  companyId: string
  companyName: string
  vin: string
  stockNumber: string
  assignedDriver: string
  price: string
  driverNotes: string
  legStatus: string
  timeInStatus?: string
  timeInStatusMinutes?: number
  customerName: string
  fromLocation: string
  toLocation: string
  hasPhotos?: boolean
}

const getLegStatusChipColor = (
  status: string
): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
  const normalizedStatus = status.trim().toLowerCase()
  if (normalizedStatus === 'completed') return 'success'
  if (normalizedStatus === 'in progress') return 'primary'
  if (normalizedStatus === 'assigned') return 'info'
  if (normalizedStatus === 'draft') return 'default'
  return 'default'
}

interface TransportersLegsGridProps {
  rowData: TransportersLegRow[]
  isFlagging: boolean
  canFlagRow: (row: TransportersLegRow) => boolean
  canOpenTimeline: (row: TransportersLegRow) => boolean
  canOpenPhotos: (row: TransportersLegRow) => boolean
  isRowFlagged: (row: TransportersLegRow) => boolean
  onRequestToggleFlag: (row: TransportersLegRow) => void
  onOpenTimeline: (row: TransportersLegRow) => void
  onOpenPhotos: (row: TransportersLegRow) => void
}

const TransportersLegsGrid: React.FC<TransportersLegsGridProps> = ({
  rowData,
  isFlagging,
  canFlagRow,
  canOpenTimeline,
  canOpenPhotos,
  isRowFlagged,
  onRequestToggleFlag,
  onOpenTimeline,
  onOpenPhotos,
}) => {
  const theme = useTheme()
  const isMobile = useIsMobile()

  const columnDefs = useMemo<ColDef<TransportersLegRow>[]>(
    () => [
      {
        headerName: 'Actions',
        cellRenderer: (params: ICellRendererParams<TransportersLegRow>) => {
          const row = params.data
          if (!row) return null
          const canFlag = canFlagRow(row)
          const canOpen = canOpenTimeline(row)
          const canOpenLegPhotos = canOpenPhotos(row)
          const isFlagged = isRowFlagged(row)
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
              <Tooltip title={isFlagged ? 'Unflag truck' : 'Flag truck'}>
                <span>
                  <IconButton
                    size="small"
                    onClick={() => onRequestToggleFlag(row)}
                    aria-label={isFlagged ? 'Unflag truck' : 'Flag truck'}
                    disabled={!canFlag || isFlagging}
                  >
                    <FlagIcon
                      fontSize="small"
                      sx={{ color: isFlagged ? 'warning.main' : 'action.disabled' }}
                    />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Open timeline">
                <span>
                  <IconButton
                    size="small"
                    onClick={() => onOpenTimeline(row)}
                    aria-label="Open timeline"
                    disabled={!canOpen}
                  >
                    <TimelineIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Open leg photos">
                <span>
                  <IconButton
                    size="small"
                    onClick={() => onOpenPhotos(row)}
                    aria-label="Open leg photos"
                    disabled={!canOpenLegPhotos}
                  >
                    <PhotoCameraIcon
                      fontSize="small"
                      sx={{ color: row.hasPhotos ? 'success.main' : 'action.disabled' }}
                    />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          )
        },
        sortable: false,
        filter: false,
        pinned: 'left',
        lockPinned: true,
        suppressMovable: true,
        flex: 0,
        minWidth: 150,
        maxWidth: 150,
      },
      {
        field: 'vin',
        headerName: 'VIN',
        flex: 0,
        minWidth: 150,
      },
      {
        field: 'stockNumber',
        headerName: 'Stock #',
        flex: 0,
        minWidth: 110,
      },
      {
        field: 'assignedDriver',
        headerName: 'Driver',
        flex: 0,
        minWidth: 170,
      },
      {
        field: 'price',
        headerName: 'Price',
        flex: 0,
        minWidth: 120,
      },
      {
        field: 'driverNotes',
        headerName: 'Driver notes',
        flex: 1,
        minWidth: 220,
      },
      {
        field: 'companyName',
        headerName: 'Transport company',
        flex: 0,
        minWidth: 180,
      },
      {
        field: 'legStatus',
        headerName: 'Leg status',
        cellRenderer: (params: ICellRendererParams<TransportersLegRow>) => {
          const status = params.data?.legStatus ?? 'N/A'
          return (
            <Chip
              label={status}
              size="small"
              color={getLegStatusChipColor(status)}
              sx={{
                fontSize: '0.7rem',
                fontWeight: 600,
                width: 'fit-content',
              }}
            />
          )
        },
        flex: 0,
        minWidth: 130,
      },
      {
        field: 'timeInStatus',
        headerName: 'Time in status',
        valueGetter: (params) => params.data?.timeInStatusMinutes ?? 0,
        cellRenderer: (params: ICellRendererParams<TransportersLegRow>) => {
          const row = params.data
          if (!row || !row.timeInStatus || row.timeInStatus === '-') {
            return (
              <Typography variant="caption" color="text.secondary">
                -
              </Typography>
            )
          }

          const minutes = row.timeInStatusMinutes ?? 0
          const color =
            minutes > 60 * 24 * 7
              ? 'error.main'
              : minutes > 60 * 24 * 3
                ? 'warning.main'
                : 'text.secondary'

          return (
            <Typography variant="caption" sx={{ color, fontWeight: 500 }}>
              {row.timeInStatus}
            </Typography>
          )
        },
        flex: 0,
        minWidth: 130,
      },
      {
        field: 'customerName',
        headerName: 'Customer',
        flex: 0,
        minWidth: 180,
      },
      {
        field: 'fromLocation',
        headerName: 'From',
        flex: 1,
        minWidth: 180,
      },
      {
        field: 'toLocation',
        headerName: 'To',
        flex: 1,
        minWidth: 180,
      },
    ],
    [
      canFlagRow,
      canOpenTimeline,
      canOpenPhotos,
      isFlagging,
      isRowFlagged,
      onOpenTimeline,
      onOpenPhotos,
      onRequestToggleFlag,
    ]
  )

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      resizable: true,
      editable: false,
    }),
    []
  )

  if (isMobile) {
    return (
      <Box sx={{ p: 1.5 }}>
        <MobileRecordList isEmpty={rowData.length === 0} empty="No transport legs match the current filters.">
          {rowData.map(row => {
            const isFlagged = isRowFlagged(row)
            return (
              <MobileRecordCard
                key={row.id}
                title={`${row.stockNumber || '—'} · ${row.vin || '—'}`}
                status={{ tone: legStatusTone(row.legStatus), label: row.legStatus }}
                meta={`${row.fromLocation} → ${row.toLocation}`}
                detail={[
                  row.timeInStatus ? `In status ${row.timeInStatus}` : null,
                  row.companyName,
                  row.assignedDriver,
                  row.customerName,
                ]
                  .filter(Boolean)
                  .join(' · ')}
                trailing={
                  <Box sx={{ display: 'flex' }}>
                    <MobileRecordAction
                      label={isFlagged ? 'Unflag truck' : 'Flag truck'}
                      onClick={() => onRequestToggleFlag(row)}
                      disabled={!canFlagRow(row) || isFlagging}
                    >
                      <FlagIcon
                        fontSize="small"
                        sx={{ color: isFlagged ? 'warning.main' : 'action.disabled' }}
                      />
                    </MobileRecordAction>
                    <MobileRecordAction
                      label="Open timeline"
                      onClick={() => onOpenTimeline(row)}
                      disabled={!canOpenTimeline(row)}
                    >
                      <TimelineIcon fontSize="small" />
                    </MobileRecordAction>
                    <MobileRecordAction
                      label="Open leg photos"
                      onClick={() => onOpenPhotos(row)}
                      disabled={!canOpenPhotos(row)}
                    >
                      <PhotoCameraIcon
                        fontSize="small"
                        sx={{ color: row.hasPhotos ? 'success.main' : 'action.disabled' }}
                      />
                    </MobileRecordAction>
                  </Box>
                }
              />
            )
          })}
        </MobileRecordList>
      </Box>
    )
  }

  return (
    <Box sx={{ height: '70vh', width: '100%', display: 'flex', flexDirection: 'column' }}>
      <div
        className={theme.palette.mode === 'dark' ? 'ag-theme-alpine-dark' : 'ag-theme-alpine'}
        style={{ height: '100%', width: '100%', flex: 1 }}
      >
        <AgGridReact<TransportersLegRow>
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          domLayout="normal"
          animateRows={false}
          suppressCellFocus
        />
      </div>
    </Box>
  )
}

export default TransportersLegsGrid
